import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { bookingService, garageService, orderService, notesService, emailService } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Search, Eye, RefreshCw, Warehouse, PackagePlus, Mail, MessageSquare, ExternalLink, Edit, Save, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { useNavigate } from 'react-router-dom';

const BookingsManager = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingNotes, setBookingNotes] = useState([]);
  const [bookingEmails, setBookingEmails] = useState([]);
  const [noteForm, setNoteForm] = useState({ author: '', content: '' });
  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', body: '', sent_by: '' });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsData, garagesData] = await Promise.all([
        bookingService.getAll(),
        garageService.getAll()
      ]);
      setBookings(bookingsData.bookings || []);
      setGarages(garagesData.garages || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingNotes = async (bookingId) => {
    try {
      const data = await notesService.getByBooking(bookingId);
      setBookingNotes(data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchBookingEmails = async (bookingId) => {
    try {
      const data = await emailService.getByBooking(bookingId);
      setBookingEmails(data.emails || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await bookingService.updateStatus(bookingId, newStatus);
      toast({
        title: 'Success',
        description: 'Booking status updated successfully'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update booking status',
        variant: 'destructive'
      });
    }
  };

  const handleEditBooking = (booking) => {
    setEditFormData({
      booking_id: booking.booking_id,
      name: booking.name || '',
      email: booking.email || '',
      mobile: booking.mobile || '',
      carBrand: booking.car_brand || '',
      carModel: booking.car_model || '',
      fuelType: booking.fuel_type || '',
      manufacturingYear: booking.manufacturing_year || '',
      service: booking.service || '',
      location: booking.location || '',
      address: booking.address || '',
      date: booking.date || '',
      timeSlot: booking.time_slot || '',
      specialRequirements: booking.special_requirements || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateBooking = async () => {
    try {
      const updateData = {
        name: editFormData.name,
        email: editFormData.email,
        mobile: editFormData.mobile,
        car_brand: editFormData.carBrand,
        car_model: editFormData.carModel,
        fuel_type: editFormData.fuelType,
        manufacturing_year: editFormData.manufacturingYear ? parseInt(editFormData.manufacturingYear) : null,
        service: editFormData.service,
        location: editFormData.location,
        address: editFormData.address,
        date: editFormData.date,
        time_slot: editFormData.timeSlot,
        special_requirements: editFormData.specialRequirements
      };

      await bookingService.update(editFormData.booking_id, updateData);
      
      toast({
        title: 'Success',
        description: 'Booking details updated successfully'
      });
      
      setIsEditDialogOpen(false);
      fetchData();
      
      // Refresh selected booking if it's the one we just updated
      if (selectedBooking && selectedBooking.booking_id === editFormData.booking_id) {
        const updatedBooking = await bookingService.getById(editFormData.booking_id);
        setSelectedBooking(updatedBooking.booking);
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: 'Error',
        description: error.detail || 'Failed to update booking',
        variant: 'destructive'
      });
    }
  };


  const handleGarageAssignment = async (bookingId, garageId) => {
    try {
      await garageService.assignToBooking(bookingId, garageId);
      toast({
        title: 'Success',
        description: 'Garage assigned successfully'
      });
      fetchData();
      if (selectedBooking && selectedBooking.booking_id === bookingId) {
        const garage = garages.find(g => g.id === garageId);
        setSelectedBooking({
          ...selectedBooking,
          garage_id: garageId,
          garage_name: garage?.name
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to assign garage',
        variant: 'destructive'
      });
    }
  };

  const handleCreateOrder = async (bookingId) => {
    try {
      const response = await orderService.createFromBooking(bookingId);
      toast({
        title: 'Success',
        description: `Order ${response.order_id} created successfully`
      });
      fetchData();
      // Navigate to order tracking
      navigate(`/order/${response.order_id}`);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.detail || 'Failed to create order',
        variant: 'destructive'
      });
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      await notesService.create(selectedBooking.booking_id, noteForm.author, noteForm.content);
      toast({
        title: 'Success',
        description: 'Note added successfully'
      });
      setNoteForm({ author: '', content: '' });
      fetchBookingNotes(selectedBooking.booking_id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add note',
        variant: 'destructive'
      });
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      const response = await emailService.send(selectedBooking.booking_id, emailForm);
      
      if (response.status === 'sent') {
        toast({
          title: 'Email Sent!',
          description: 'Email sent successfully to customer'
        });
      } else {
        toast({
          title: 'Email Logged',
          description: 'Email logged but sending failed',
          variant: 'destructive'
        });
      }
      
      setEmailForm({ to_email: selectedBooking.customer?.email || selectedBooking.email || '', subject: '', body: '', sent_by: '' });
      fetchBookingEmails(selectedBooking.booking_id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send email',
        variant: 'destructive'
      });
    }
  };

  const filteredBookings = bookings.filter(booking => {
    // Handle both nested (customer.name) and flat (customer_name) structures
    const customerName = booking.customer?.name || booking.customer_name || '';
    const customerMobile = booking.customer?.mobile || booking.mobile || '';
    const bookingId = booking.booking_id || booking.id || '';
    
    const matchesSearch = 
      bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerMobile.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFrom || dateTo) {
      const bookingDate = new Date(booking.created_at || booking.date);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchesDate = matchesDate && bookingDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire day
        matchesDate = matchesDate && bookingDate <= toDate;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-blue-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading bookings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Bookings</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-blue-600">{stats.confirmed}</div>
            <div className="text-sm text-gray-600">Confirmed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <div className="text-sm text-gray-600">Cancelled</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Bookings</span>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by booking ID, name, or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-3 items-center flex-wrap">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex gap-2">
                <Input
                  type="date"
                  placeholder="From date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                />
                <Input
                  type="date"
                  placeholder="To date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                />
              </div>
              
              {(statusFilter !== 'all' || dateFrom || dateTo) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          {/* Bookings Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Booking ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Garage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No bookings found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBookings.map((booking) => {
                    // Handle both nested and flat structures
                    const bookingId = booking.booking_id || booking.id;
                    const customerName = booking.customer?.name || booking.customer_name;
                    const customerMobile = booking.customer?.mobile || booking.mobile;
                    const carBrand = booking.car_details?.brand || booking.car_brand;
                    const carModel = booking.car_details?.model || booking.car_model;
                    const fuelType = booking.car_details?.fuel_type || booking.fuel_type;
                    const manufacturingYear = booking.car_details?.manufacturing_year || booking.manufacturing_year;
                    
                    return (
                    <TableRow key={bookingId}>
                      <TableCell className="font-mono text-sm">{bookingId}</TableCell>
                      <TableCell>{customerName}</TableCell>
                      <TableCell>{customerMobile}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{carBrand} {carModel}</div>
                          {fuelType && (
                            <div className="text-xs text-gray-500">{fuelType}</div>
                          )}
                          {manufacturingYear && (
                            <div className="text-xs text-gray-500">{manufacturingYear}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{booking.service?.service_name || booking.service}</TableCell>
                      <TableCell>{booking.schedule?.date || booking.pickup_date}</TableCell>
                      <TableCell>
                        {booking.garage_name ? (
                          <span className="text-sm text-green-600 font-medium">{booking.garage_name}</span>
                        ) : (
                          <span className="text-sm text-gray-400">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(booking.status)} text-white`}>
                          {booking.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  const bid = booking.booking_id || booking.id;
                                  fetchBookingNotes(bid);
                                  fetchBookingEmails(bid);
                                  const email = booking.customer?.email || booking.email || '';
                                  setEmailForm({ ...emailForm, to_email: email });
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center justify-between">
                                  <span>Booking Details - {selectedBooking?.booking_id}</span>
                                  {selectedBooking?.order_id && (
                                    <Button 
                                      size="sm" 
                                      onClick={() => navigate(`/order/${selectedBooking.order_id}`)}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <ExternalLink className="w-4 h-4 mr-2" />
                                      View Order
                                    </Button>
                                  )}
                                </DialogTitle>
                              </DialogHeader>
                              {selectedBooking && (
                                <Tabs defaultValue="details" className="w-full">
                                  <TabsList className="grid w-full grid-cols-4">
                                    <TabsTrigger value="details">Details</TabsTrigger>
                                    <TabsTrigger value="notes">
                                      <MessageSquare className="w-4 h-4 mr-2" />
                                      Notes
                                    </TabsTrigger>
                                    <TabsTrigger value="emails">
                                      <Mail className="w-4 h-4 mr-2" />
                                      Emails
                                    </TabsTrigger>
                                    <TabsTrigger value="actions">Actions</TabsTrigger>
                                  </TabsList>

                                  <TabsContent value="details" className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <p className="text-sm text-gray-600">Booking ID</p>
                                        <p className="font-semibold">{selectedBooking.booking_id}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Status</p>
                                        <Badge className={`${getStatusColor(selectedBooking.status)} text-white`}>
                                          {selectedBooking.status}
                                        </Badge>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Customer Name</p>
                                        <p className="font-semibold">{selectedBooking.customer?.name || selectedBooking.customer_name}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Mobile</p>
                                        <p className="font-semibold">{selectedBooking.customer?.mobile || selectedBooking.mobile}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Email</p>
                                        <p className="font-semibold">{selectedBooking.customer?.email || selectedBooking.email || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Vehicle</p>
                                        <p className="font-semibold">
                                          {selectedBooking.car_details?.brand || selectedBooking.car_brand} {selectedBooking.car_details?.model || selectedBooking.car_model}
                                        </p>
                                        {(selectedBooking.car_details?.fuel_type || selectedBooking.fuel_type) && (
                                          <p className="text-sm text-gray-600 mt-1">
                                            Fuel: {selectedBooking.car_details?.fuel_type || selectedBooking.fuel_type}
                                          </p>
                                        )}
                                        {(selectedBooking.car_details?.manufacturing_year || selectedBooking.manufacturing_year) && (
                                          <p className="text-sm text-gray-600">
                                            Year: {selectedBooking.car_details?.manufacturing_year || selectedBooking.manufacturing_year}
                                          </p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Service</p>
                                        <p className="font-semibold">{selectedBooking.service?.service_name || selectedBooking.service}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Package</p>
                                        <p className="font-semibold">{selectedBooking.service?.package_name}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Price</p>
                                        <p className="font-semibold">₹{selectedBooking.service?.price}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Date</p>
                                        <p className="font-semibold">{selectedBooking.schedule?.date || selectedBooking.pickup_date}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Time Slot</p>
                                        <p className="font-semibold">{selectedBooking.schedule?.time_slot || selectedBooking.time_slot}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Location</p>
                                        <p className="font-semibold">{selectedBooking.schedule?.location || selectedBooking.location}</p>
                                      </div>
                                      <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Address</p>
                                        <p className="font-semibold">{selectedBooking.schedule?.address || selectedBooking.address || 'N/A'}</p>
                                      </div>
                                    </div>
                                  </TabsContent>

                                  <TabsContent value="notes" className="space-y-4">
                                    <form onSubmit={handleAddNote} className="space-y-3 p-4 bg-gray-50 rounded-lg">
                                      <div>
                                        <Label>Your Name</Label>
                                        <Input
                                          value={noteForm.author}
                                          onChange={(e) => setNoteForm({ ...noteForm, author: e.target.value })}
                                          placeholder="Your name"
                                          required
                                        />
                                      </div>
                                      <div>
                                        <Label>Note</Label>
                                        <Textarea
                                          value={noteForm.content}
                                          onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                                          placeholder="Add internal note..."
                                          rows={3}
                                          required
                                        />
                                      </div>
                                      <Button type="submit" size="sm">
                                        Add Note
                                      </Button>
                                    </form>
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                      {bookingNotes.length === 0 ? (
                                        <p className="text-center text-gray-500 py-4">No notes yet</p>
                                      ) : (
                                        bookingNotes.map(note => (
                                          <div key={note.id} className="p-3 bg-white border rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="font-semibold text-sm">{note.author}</span>
                                              <span className="text-xs text-gray-500">
                                                {new Date(note.created_at).toLocaleString()}
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-700">{note.content}</p>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </TabsContent>

                                  <TabsContent value="emails" className="space-y-4">
                                    <form onSubmit={handleSendEmail} className="space-y-3 p-4 bg-gray-50 rounded-lg">
                                      <div className="grid grid-cols-2 gap-3">
                                        <div>
                                          <Label>To Email</Label>
                                          <Input
                                            type="email"
                                            value={emailForm.to_email}
                                            onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                                            required
                                          />
                                        </div>
                                        <div>
                                          <Label>From (Your Name)</Label>
                                          <Input
                                            value={emailForm.sent_by}
                                            onChange={(e) => setEmailForm({ ...emailForm, sent_by: e.target.value })}
                                            placeholder="Your name"
                                            required
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <Label>Subject</Label>
                                        <Input
                                          value={emailForm.subject}
                                          onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                                          placeholder="Email subject"
                                          required
                                        />
                                      </div>
                                      <div>
                                        <Label>Message</Label>
                                        <Textarea
                                          value={emailForm.body}
                                          onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                                          placeholder="Email content..."
                                          rows={4}
                                          required
                                        />
                                      </div>
                                      <Button type="submit" size="sm">
                                        <Mail className="w-4 h-4 mr-2" />
                                        Log Email
                                      </Button>
                                    </form>
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                      {bookingEmails.length === 0 ? (
                                        <p className="text-center text-gray-500 py-4">No emails sent yet</p>
                                      ) : (
                                        bookingEmails.map(email => (
                                          <div key={email.id} className="p-3 bg-white border rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="font-semibold text-sm">{email.subject}</span>
                                              <Badge variant="outline">{email.status}</Badge>
                                            </div>
                                            <p className="text-xs text-gray-600 mb-2">
                                              To: {email.to_email} | From: {email.sent_by}
                                            </p>
                                            <p className="text-sm text-gray-700">{email.body}</p>
                                            <p className="text-xs text-gray-500 mt-2">
                                              {new Date(email.created_at).toLocaleString()}
                                            </p>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </TabsContent>

                                  <TabsContent value="actions" className="space-y-4">
                                    <div className="pb-4 border-b">
                                      <Button 
                                        onClick={() => handleEditBooking(selectedBooking)}
                                        className="w-full bg-blue-600 hover:bg-blue-700"
                                        disabled={selectedBooking.status !== 'pending'}
                                      >
                                        <Edit className="w-4 h-4 mr-2" />
                                        Edit Booking Details
                                      </Button>
                                      {selectedBooking.status !== 'pending' && (
                                        <p className="text-xs text-gray-500 mt-2 text-center">
                                          Edit only available for pending bookings
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div>
                                      <p className="text-sm text-gray-600 mb-2">Assign Garage</p>
                                      <Select 
                                        value={selectedBooking.garage_id || ''}
                                        onValueChange={(value) => handleGarageAssignment(selectedBooking.booking_id, value)}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select garage" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {garages.filter(g => g.is_active).map(garage => (
                                            <SelectItem key={garage.id} value={garage.id}>
                                              <div className="flex items-center gap-2">
                                                <Warehouse className="w-4 h-4" />
                                                {garage.name} - {garage.location}
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      {selectedBooking.garage_name && (
                                        <p className="text-xs text-green-600 mt-1">
                                          Currently assigned: {selectedBooking.garage_name}
                                        </p>
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-600 mb-2">Update Status</p>
                                      <Select 
                                        value={selectedBooking.status}
                                        onValueChange={(value) => handleStatusUpdate(selectedBooking.booking_id, value)}
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="confirmed">Confirmed</SelectItem>
                                          <SelectItem value="cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {selectedBooking.status === 'confirmed' && !selectedBooking.order_id && (
                                      <div className="pt-4 border-t">
                                        <Button 
                                          onClick={() => handleCreateOrder(selectedBooking.booking_id)}
                                          className="w-full bg-green-600 hover:bg-green-700"
                                        >
                                          <PackagePlus className="w-4 h-4 mr-2" />
                                          Create Order & Start Tracking
                                        </Button>
                                        <p className="text-xs text-gray-500 mt-2">This will create an order and enable tracking</p>
                                      </div>
                                    )}
                                  </TabsContent>
                                </Tabs>
                              )}
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Booking Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Booking Details - {editFormData.booking_id}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Mobile *</Label>
                <Input
                  value={editFormData.mobile}
                  onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Car Brand *</Label>
                <Input
                  value={editFormData.carBrand}
                  onChange={(e) => setEditFormData({ ...editFormData, carBrand: e.target.value })}
                />
              </div>
              <div>
                <Label>Car Model *</Label>
                <Input
                  value={editFormData.carModel}
                  onChange={(e) => setEditFormData({ ...editFormData, carModel: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fuel Type</Label>
                <Input
                  value={editFormData.fuelType}
                  onChange={(e) => setEditFormData({ ...editFormData, fuelType: e.target.value })}
                />
              </div>
              <div>
                <Label>Manufacturing Year</Label>
                <Input
                  type="number"
                  value={editFormData.manufacturingYear}
                  onChange={(e) => setEditFormData({ ...editFormData, manufacturingYear: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Service *</Label>
              <Input
                value={editFormData.service}
                onChange={(e) => setEditFormData({ ...editFormData, service: e.target.value })}
              />
            </div>

            <div>
              <Label>Location</Label>
              <Input
                value={editFormData.location}
                onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
              />
            </div>

            <div>
              <Label>Pickup Address</Label>
              <Input
                value={editFormData.address}
                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preferred Date</Label>
                <Input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Preferred Time</Label>
                <Input
                  value={editFormData.timeSlot}
                  onChange={(e) => setEditFormData({ ...editFormData, timeSlot: e.target.value })}
                  placeholder="e.g., 09:00 AM - 11:00 AM"
                />
              </div>
            </div>

            <div>
              <Label>Special Requirements</Label>
              <Textarea
                value={editFormData.specialRequirements}
                onChange={(e) => setEditFormData({ ...editFormData, specialRequirements: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateBooking} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsManager;
